import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { CompanyProfile as Profile } from "../../types/company";
import type { CompanyDocumentKey } from "../../types/vtc";
import { COMPANY_BUSINESS_TYPES, DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES } from "../../data/auth";
import { updateProfile } from "../../lib/company";
import { ApiError, errorText, focusFirstError } from "../../lib/api";
import {
  getCompanyProfile,
  submitVerification,
  updateCompanyProfile,
  uploadCompanyDocument,
} from "../../lib/companyApi";
import { useCompany } from "../../lib/companyContext";
import { longDate } from "../../lib/format";
import type { AddressParts } from "../../lib/googlePlaces";
import AddressField from "../../components/ui/AddressField";
import ChangeEmail from "../../components/ui/ChangeEmail";
import { FormAlert, SelectField, TextField } from "../../components/ui/FormField";
import { ActionButton, Card, CardTitle, Hint, PageTitle, StatusBadge } from "../../components/company/ui";
import { useT } from "../../i18n";

type FormKey =
  | "firstName" | "lastName" | "email" | "phone" | "name" | "businessType"
  | "siret" | "vat" | "address" | "city" | "postalCode" | "region" | "country";
type FormValues = Pick<Profile, FormKey>;

const FORM_KEYS: FormKey[] = [
  "firstName", "lastName", "email", "phone", "name", "businessType",
  "siret", "vat", "address", "city", "postalCode", "region", "country",
];
const pickForm = (p: Profile) =>
  Object.fromEntries(FORM_KEYS.map((k) => [k, p[k]])) as FormValues;

/** What "Submit for verification" needs besides the documents (same as the backend). */
const requiredFields = (p: Profile): FormKey[] =>
  p.ownerType === "individual"
    ? ["address", "city", "postalCode", "country"]
    : ["name", "siret", "address", "city", "postalCode", "country"];

export default function CompanyProfile() {
  const t = useT();
  const pr = t.company.profile;
  const navigate = useNavigate();
  const { state, run, toast } = useCompany();
  const { profile } = state;
  const isCompany = profile.ownerType !== "individual";

  const [form, setForm] = useState<FormValues>(() => pickForm(profile));
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState<CompanyDocumentKey | null>(null);

  // Refill the form when the profile arrives from the API (or after a save).
  const [shownProfile, setShownProfile] = useState(profile);
  if (profile !== shownProfile) {
    setShownProfile(profile);
    setForm(pickForm(profile));
  }

  const set = (key: FormKey, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
    setSaveError("");
  };

  /** Google suggestion → street, city, postal code, region, country. */
  const fillAddress = (parts: AddressParts) =>
    setForm((f) => ({
      ...f,
      address: parts.street || f.address,
      city: parts.city,
      postalCode: parts.postalCode,
      region: parts.state,
      country: parts.country,
    }));

  const canEditDocs = profile.verification === "incomplete" || profile.verification === "rejected";
  const missingFields = canEditDocs ? requiredFields(profile).filter((k) => !profile[k]) : [];
  const fieldLabel = (k: FormKey) => {
    const label = k === "name" ? pr.fields.name : pr.fields[k as keyof typeof pr.fields];
    return label === label.toUpperCase() ? label : label.toLowerCase();
  };

  const isDirty = FORM_KEYS.some((k) => form[k] !== pickForm(profile)[k]);

  /** Checks what can be checked here, before asking the backend. */
  const validate = () => {
    const e: Partial<Record<FormKey, string>> = {};
    if (!form.firstName.trim()) e.firstName = t.common.required;
    if (!form.lastName.trim()) e.lastName = t.common.required;
    if (isCompany && !form.name.trim()) e.name = t.common.required;
    const siret = form.siret.replace(/\s/g, "");
    if (isCompany && siret && !/^\d{14}$/.test(siret)) e.siret = t.company.convert.siretInvalid;
    return e;
  };

  /** PUT /company/profile; returns false (and shows why) when it fails. */
  const save = async (quiet = false): Promise<boolean> => {
    const invalid = validate();
    if (Object.keys(invalid).length) {
      setErrors(invalid);
      setSaveError(Object.values(invalid).join(" · "));
      focusFirstError();
      return false;
    }
    setSaveError("");
    setSaving(true);
    try {
      const changes = Object.fromEntries(
        FORM_KEYS.filter((k) => isCompany || k !== "businessType").map((k) => [k, form[k].trim()]),
      );
      const { profile: saved, message } = await updateCompanyProfile(changes);
      run(updateProfile, saved);
      if (!quiet) toast.success(message || pr.saved);
      return true;
    } catch (err) {
      // e.g. { siret: "SIRET must be 14 digits" }: shown under the field and in the alert
      if (err instanceof ApiError && err.errors) {
        setErrors(err.errors);
        focusFirstError();
      }
      const message = errorText(err);
      setSaveError(message);
      toast.error(message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => run(updateProfile, await getCompanyProfile());

  const upload = async (key: CompanyDocumentKey, file: File) => {
    if (file.size > DOCUMENT_MAX_BYTES) return toast.error(t.auth.companySignup.fileTooLarge);
    if (!/\.(pdf|jpe?g|png)$/i.test(file.name)) return toast.error(t.auth.companySignup.fileType);
    setUploading(key);
    try {
      toast.success(await uploadCompanyDocument(key, file));
      await refresh();
    } catch (err) {
      toast.error(err);
    } finally {
      setUploading(null);
    }
  };

  /** Saves pending edits first, so the backend checks what is on screen. */
  const submit = async () => {
    if (isDirty && !(await save(true))) return;
    setSubmitting(true);
    try {
      const { profile: submitted, message } = await submitVerification();
      run(updateProfile, submitted);
      toast.success(message);
    } catch (err) {
      if (err instanceof ApiError && err.errors) {
        setErrors(err.errors);
        focusFirstError();
      }
      toast.error(errorText(err));
    } finally {
      setSubmitting(false);
    }
  };

  const businessOptions = isCompany ? COMPANY_BUSINESS_TYPES : (["individual-rental"] as const);
  const ownerLabel = t.auth.companySignup.ownerTypes[profile.ownerType].label;
  const businessLabel = profile.businessType ? t.auth.companySignup.businessTypes[profile.businessType] : "";

  return (
    <>
      <PageTitle>{pr.title}</PageTitle>
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Card className="p-6">
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <CardTitle className="mb-0.5">{pr.accountInfo}</CardTitle>
            {profile.createdAt && (
              <p className="m-0 mb-4 text-[13px] text-muted">{pr.createdOn(longDate(profile.createdAt))}</p>
            )}
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              <TextField label={pr.fields.firstName} placeholder={t.common.ph.firstName} autoComplete="given-name" value={form.firstName} error={errors.firstName} onChange={(e) => set("firstName", e.target.value)} />
              <TextField label={pr.fields.lastName} placeholder={t.common.ph.lastName} autoComplete="family-name" value={form.lastName} error={errors.lastName} onChange={(e) => set("lastName", e.target.value)} />
              <ChangeEmail
                label={t.auth.companySignup.emailAddress}
                email={form.email}
                onChanged={(email) => {
                  set("email", email);
                  run(updateProfile, { email });
                }}
              />
              <TextField label={t.auth.companySignup.phoneNumber} type="tel" placeholder={t.common.ph.phone} autoComplete="tel" value={form.phone} error={errors.phone} onChange={(e) => set("phone", e.target.value)} />
              {isCompany && (
                <TextField full label={pr.fields.name} placeholder={t.common.ph.companyName} autoComplete="organization" value={form.name} error={errors.name} onChange={(e) => set("name", e.target.value)} />
              )}
              <TextField label={pr.accountType} value={ownerLabel} readOnly />
              <SelectField
                label={pr.businessType}
                options={businessOptions}
                labels={t.auth.companySignup.businessTypes}
                value={form.businessType}
                disabled={!isCompany || !canEditDocs}
                onChange={(e) => set("businessType", e.target.value)}
              />
            </div>

            <hr className="my-6 border-0 border-t border-line" />

            <CardTitle className="mb-3">{isCompany ? pr.companyDetails : pr.publicProfile}</CardTitle>
            {missingFields.length > 0 && (
              <div className="mb-3.5">
                <FormAlert tone="warning">{pr.requiredForVerification(missingFields.map(fieldLabel).join(", "))}</FormAlert>
              </div>
            )}
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              {isCompany ? (
                <>
                  <TextField
                    label={pr.fields.siret}
                    inputMode="numeric"
                    placeholder={t.common.ph.siret}
                    value={form.siret}
                    error={errors.siret}
                    disabled={profile.verification === "verified"}
                    onChange={(e) => set("siret", e.target.value)}
                  />
                  <TextField label={pr.vatOptional} placeholder={t.common.ph.vat} value={form.vat} error={errors.vat} onChange={(e) => set("vat", e.target.value)} />
                </>
              ) : (
                <TextField full label={pr.displayName} placeholder={t.common.ph.displayName} value={form.name} error={errors.name} onChange={(e) => set("name", e.target.value)} />
              )}
              <AddressField
                full
                label={pr.fields.address}
                placeholder={t.auth.companySignup.addressPlaceholder}
                autoComplete="street-address"
                value={form.address}
                error={errors.address}
                onAddressSelect={fillAddress}
                onChange={(e) => set("address", e.target.value)}
              />
              <TextField label={pr.fields.city} placeholder={t.common.ph.city} autoComplete="address-level2" value={form.city} error={errors.city} onChange={(e) => set("city", e.target.value)} />
              <TextField label={pr.fields.postalCode} placeholder={t.common.ph.postalCode} autoComplete="postal-code" value={form.postalCode} error={errors.postalCode} onChange={(e) => set("postalCode", e.target.value)} />
              <TextField label={pr.fields.region} placeholder={t.common.ph.region} autoComplete="address-level1" value={form.region} error={errors.region} onChange={(e) => set("region", e.target.value)} />
              <TextField label={pr.fields.country} placeholder={t.common.ph.country} autoComplete="country-name" value={form.country} error={errors.country} onChange={(e) => set("country", e.target.value)} />
            </div>

            {saveError && (
              <div className="mt-4">
                <FormAlert>{saveError}</FormAlert>
              </div>
            )}
            <ActionButton type="submit" variant="primary" size="lg" disabled={saving} className="mt-5 px-5.5">
              {saving ? pr.saving : pr.save}
            </ActionButton>
          </form>
          <Hint className="mt-3.5">{pr.hint}</Hint>
        </Card>

        <div className="grid gap-4">
          <Card>
            <div className="mb-2 flex items-start justify-between gap-3">
              <div>
                <CardTitle className="mb-0.5">{pr.verification}</CardTitle>
                <div className="text-xs text-muted">{pr.verificationSubtitle(ownerLabel, businessLabel)}</div>
              </div>
              <StatusBadge kind="verification" status={profile.verification} className="shrink-0" />
            </div>

            {profile.docs.map((doc) => (
              <DocumentRow
                key={doc.key}
                label={t.auth.documents[doc.key].label}
                fileName={doc.fileName}
                badge={<StatusBadge kind="companyDoc" status={doc.status} className="px-1.75 py-0.75 text-[11px]" />}
                error={errors[doc.key]}
                canUpload={canEditDocs}
                uploading={uploading === doc.key}
                buttonLabel={uploading === doc.key ? pr.uploading : doc.status === "missing" ? pr.upload : pr.replace}
                onFile={(file) => void upload(doc.key, file)}
              />
            ))}

            {profile.verification === "rejected" && (
              <p className="mt-3 mb-0 text-[13px] leading-normal text-tone-red-fg">
                {pr.rejectedText}
                {profile.reviewNote && <> {pr.reviewNote(profile.reviewNote)}</>}
              </p>
            )}
            {canEditDocs ? (
              <>
                <Hint className="mt-3">{pr.uploadThenSubmit}</Hint>
                <ActionButton
                  variant="primary"
                  size="lg"
                  className="mt-3 w-full"
                  disabled={submitting || saving || uploading !== null}
                  onClick={() => void submit()}
                >
                  {submitting ? pr.submitting : pr.submitForVerification}
                </ActionButton>
              </>
            ) : profile.verification === "pending" ? (
              profile.submittedAt && <Hint className="mt-3">{pr.submittedOn(longDate(profile.submittedAt))}</Hint>
            ) : (
              <Hint className="mt-3">{pr.verifiedText}</Hint>
            )}
          </Card>

          {!isCompany && (
            <Card>
              <CardTitle className="mb-1">{pr.convertTitle}</CardTitle>
              <p className="m-0 mb-3.5 text-[13px] leading-normal text-muted">{pr.convertText}</p>
              <ActionButton variant="primary" size="lg" className="w-full" onClick={() => navigate("/company/profile/convert")}>
                {pr.convertButton}
              </ActionButton>
            </Card>
          )}

          <Card>
            <CardTitle className="mb-1.5">{pr.users}</CardTitle>
            <p className="m-0 text-[13px] leading-normal text-muted">
              {pr.usersText(profile.contact || profile.email)}
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}

interface DocumentRowProps {
  label: string;
  fileName?: string;
  badge: ReactNode;
  error?: string;
  canUpload: boolean;
  uploading: boolean;
  buttonLabel: string;
  onFile: (file: File) => void;
}

/** One verification document: name, uploaded file, status, Upload/Replace. */
function DocumentRow({ label, fileName, badge, error, canUpload, uploading, buttonLabel, onFile }: DocumentRowProps) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="border-t border-[#F0F0F0] py-2.5">
      <div className="flex items-center gap-2.5 text-[13px]">
        <div className="min-w-0 flex-1">
          <div>{label}</div>
          {fileName && <div className="truncate text-[11px] text-faint">{fileName}</div>}
        </div>
        {badge}
        {canUpload && (
          <>
            <ActionButton size="sm" className="font-normal" disabled={uploading} onClick={() => input.current?.click()}>
              {buttonLabel}
            </ActionButton>
            <input
              ref={input}
              type="file"
              accept={DOCUMENT_ACCEPT}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onFile(file);
                e.target.value = ""; // allow picking the same file again
              }}
            />
          </>
        )}
      </div>
      {error && <p className="mt-1 mb-0 text-xs text-tone-red-fg">{error}</p>}
    </div>
  );
}
